// admin/src/features/preview/PreviewModal.tsx
//
// Full-window preview of a photo/video inside the app (and, for exercise
// media, on the watch), at real size. Before an upload it shows the
// compressed file and the bytes saved, with Upload / Cancel; for media
// that's already live it's view-only.
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../../components/Button';
import { formatBytes } from '../../lib/image';
import { CalibrationPanel } from './CalibrationPanel';
import { useCssPxPerMm } from './calibration';
import { DeviceFrame } from './DeviceFrame';
import { PHONES, WATCHES, type DeviceKind } from './devices';
import { Glyph } from './icons';
import { CoverScreen, ExerciseListScreen, ExercisePageScreen } from './PhoneScreens';
import { watchStill, type ExerciseSubject, type PreviewSubject } from './subject';
import { WatchHowToScreen } from './WatchScreens';
import './preview.css';

type Props = {
  subject: PreviewSubject;
  /** Set for a pending upload: original vs compressed size. */
  stats?: { originalBytes: number; finalBytes: number } | null;
  onConfirm?: () => void;
  confirming?: boolean;
  onClose: () => void;
};

function asExercise(subject: PreviewSubject): ExerciseSubject | null {
  if (subject.kind === 'exercise') return subject;
  if (subject.kind === 'equipment') {
    return { kind: 'exercise', name: subject.name, muscleGroup: 'full_body', muscles: [], sport: 'strength', description: null, equipment: [{ id: 'this', name: subject.name, photoUrl: subject.photoUrl }], items: [], startIndex: 0 };
  }
  return null;
}

function Segmented<T extends string>({ value, options, onChange }: { value: T; options: Array<{ value: T; label: string; icon?: 'Smartphone' | 'Watch' }>; onChange: (v: T) => void }) {
  return (
    <div className="pv-seg" role="tablist">
      {options.map((o) => (
        <button key={o.value} type="button" role="tab" aria-selected={o.value === value} className={o.value === value ? 'active' : ''} onClick={() => onChange(o.value)}>
          {o.icon ? <Glyph name={o.icon} size={15} /> : null}
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function PreviewModal({ subject, stats, onConfirm, confirming, onClose }: Props) {
  const { cssPxPerMm, screenName, save } = useCssPxPerMm();
  const [calibrating, setCalibrating] = useState(false);
  const [target, setTarget] = useState<DeviceKind>('phone');
  const [phoneId, setPhoneId] = useState(PHONES[0].id);
  const [watchId, setWatchId] = useState(WATCHES[0].id);
  const [screen, setScreen] = useState<'page' | 'list'>('page');

  const hasWatch = subject.kind !== 'cover';
  const device = target === 'watch' && hasWatch ? WATCHES.find((d) => d.id === watchId)! : PHONES.find((d) => d.id === phoneId)!;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (calibrating) setCalibrating(false);
      else if (!confirming) onClose();
    };
    window.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
    };
  }, [calibrating, confirming, onClose]);

  const exercise = asExercise(subject);
  let content;
  if (device.kind === 'watch' && subject.kind === 'exercise') {
    content = <WatchHowToScreen device={device} name={subject.name} sport={subject.sport} stillUrl={watchStill(subject.items, subject.startIndex)} description={subject.description} />;
  } else if (device.kind === 'watch' && subject.kind === 'equipment') {
    content = <WatchHowToScreen device={device} name={subject.name} sport="strength" stillUrl={subject.photoUrl} description={null} />;
  } else if (subject.kind === 'cover') {
    content = <CoverScreen device={device} subject={subject} />;
  } else if (exercise && subject.kind === 'exercise' && screen === 'list') {
    content = <ExerciseListScreen device={device} subject={exercise} />;
  } else {
    content = <ExercisePageScreen device={device} subject={exercise!} />;
  }

  return createPortal(
    <div className="pv-overlay" role="dialog" aria-modal="true" aria-label="Preview">
      <div className="pv-toolbar">
        {hasWatch ? (
          <Segmented
            value={target}
            onChange={setTarget}
            options={[
              { value: 'phone', label: 'Phone', icon: 'Smartphone' },
              { value: 'watch', label: 'Watch', icon: 'Watch' },
            ]}
          />
        ) : null}
        {device.kind === 'phone' ? (
          <Segmented value={phoneId} onChange={setPhoneId} options={PHONES.map((d) => ({ value: d.id, label: d.label }))} />
        ) : (
          <Segmented value={watchId} onChange={setWatchId} options={WATCHES.map((d) => ({ value: d.id, label: d.label }))} />
        )}
        {subject.kind === 'exercise' && device.kind === 'phone' ? (
          <Segmented
            value={screen}
            onChange={setScreen}
            options={[
              { value: 'page', label: 'Page' },
              { value: 'list', label: 'List' },
            ]}
          />
        ) : null}
        <span className="pv-spacer" />
        {stats ? (
          <span className="pv-stats" title="Original → uploaded">
            {stats.finalBytes < stats.originalBytes ? <s>{formatBytes(stats.originalBytes)}</s> : null}
            {formatBytes(stats.finalBytes)}
          </span>
        ) : null}
        <button type="button" className="pv-icon-btn" onClick={() => setCalibrating(true)} aria-label="Real size" title={`Real size: ${screenName}`}>
          <Glyph name="Ruler" size={18} />
        </button>
        <Button tone="neutral" onClick={onClose} disabled={confirming}>
          {onConfirm ? 'Cancel' : 'Close'}
        </Button>
        {onConfirm ? (
          <Button onClick={onConfirm} disabled={confirming}>
            {confirming ? 'Uploading…' : 'Upload'}
          </Button>
        ) : null}
      </div>

      <div className="pv-stage">
        <DeviceFrame key={device.id} device={device} cssPxPerMm={cssPxPerMm}>
          {content}
        </DeviceFrame>
      </div>

      {calibrating ? (
        <div className="pv-calibrate-backdrop" onClick={() => setCalibrating(false)}>
          <CalibrationPanel
            initial={cssPxPerMm}
            onDone={(v) => {
              save(v);
              setCalibrating(false);
            }}
          />
        </div>
      ) : null}
    </div>,
    document.body,
  );
}
